import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api-client";
import type { TeamMember } from "@/types";
import type { ProjectCardWithPermissionsDto } from "./dtos/project.dto";
import {
  AddMemberSchema,
  CreateTeamSchema,
  UpdateMemberRoleSchema,
  UpdateTeamSchema,
  type AddMemberInput,
  type CreateTeamInput,
  type TeamDto,
  type UpdateMemberRoleInput,
  type UpdateTeamInput,
} from "./dtos/team.dto";
import { validateSchema } from "./dtos/validation";
import type { PortfolioCardWithPermissionsDto } from "./dtos/portfolio.dto";
import type { ChallengeCardWithPermissionsDto } from "./dtos/challenge.dto";
import { userKeys } from "./users";

// Query Keys
export const teamKeys = {
  all: ["teams"] as const,
  detail: (id: string) => [...teamKeys.all, "detail", id] as const,
  detailWithMembers: (id: string) =>
    [...teamKeys.all, "detail-with-members", id] as const,
  projects: (teamId: string) => [...teamKeys.all, teamId, "projects"] as const,
  memberships: () => [...teamKeys.all, "memberships"] as const,
  portfolios: (teamId: string) =>
    [...teamKeys.all, teamId, "portfolios"] as const,
  fundingPools: (teamId: string) =>
    [...teamKeys.all, teamId, "funding-pools"] as const,
};

// ============ QUERIES ============

/**
 * Get team
 * Uses: GET /teams/:id
 */
export function useTeam(teamId: string) {
  return useQuery({
    queryKey: teamKeys.detail(teamId),
    queryFn: async () => {
      const response = await apiClient.get<TeamDto>(`/teams/${teamId}`);
      return response.data;
    },
    enabled: !!teamId,
  });
}

/**
 * Get team members
 * Uses: GET /teams/:id/members
 */
export function useTeamMembers(teamId: string | undefined) {
  return useQuery({
    queryKey: teamKeys.detailWithMembers(teamId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<TeamMember[]>(
        `/teams/${teamId}/members`,
      );
      return response.data;
    },
    enabled: !!teamId,
  });
}

/**
 * Get all projects for a team
 * Uses: GET /teams/:id/projects
 */
export function useTeamProjects(teamId: string) {
  return useQuery({
    queryKey: teamKeys.projects(teamId),
    queryFn: async (): Promise<ProjectCardWithPermissionsDto[]> => {
      const response = await apiClient.get(`/teams/${teamId}/projects`);
      return response.data;
    },
    enabled: !!teamId,
  });
}

/**
 * Get all portfolios for a team
 * Uses: GET /teams/:id/portfolios
 */
export function useTeamPortfolios(teamId: string) {
  return useQuery({
    queryKey: teamKeys.portfolios(teamId),
    queryFn: async (): Promise<PortfolioCardWithPermissionsDto[]> => {
      const response = await apiClient.get(`/teams/${teamId}/portfolios`);
      return response.data;
    },
    enabled: !!teamId,
  });
}

/**
 * Get all funding pools for a team
 * Uses: GET /teams/:id/portfolios
 */
export function useTeamFundingPools(teamId: string) {
  return useQuery({
    queryKey: teamKeys.fundingPools(teamId),
    queryFn: async (): Promise<ChallengeCardWithPermissionsDto[]> => {
      const response = await apiClient.get(`/teams/${teamId}/challenges`);
      return response.data;
    },
    enabled: !!teamId,
  });
}

// ============ MUTATIONS ============

/**
 * Create a new team
 * Uses: POST /teams
 */
export function useCreateTeamMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTeamInput): Promise<TeamDto> => {
      const dto = validateSchema(CreateTeamSchema, data);
      const response = await apiClient.post("/teams", dto);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.teams() });
    },
  });
}

/**
 * Update team details
 * Uses: PATCH /teams/:id
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      teamId,
      data,
    }: {
      teamId: string;
      data: UpdateTeamInput;
    }): Promise<TeamDto> => {
      const dto = validateSchema(UpdateTeamSchema, data);
      const response = await apiClient.patch(`/teams/${teamId}`, dto);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: teamKeys.detail(data.id),
      });
      queryClient.invalidateQueries({
        queryKey: teamKeys.detailWithMembers(data.id),
      });
      queryClient.invalidateQueries({ queryKey: userKeys.teams() });
    },
  });
}

/**
 * Delete a team
 * Uses: DELETE /teams/:id
 */
export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamId: string): Promise<void> => {
      await apiClient.delete(`/teams/${teamId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.teams() });
    },
  });
}

/**
 * Add a member to the team
 * Uses: POST /teams/:id/members
 */
export function useAddMember(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddMemberInput) => {
      const dto = validateSchema(AddMemberSchema, data);
      const response = await apiClient.post(`/teams/${teamId}/members`, dto);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamKeys.detailWithMembers(teamId),
      });
    },
  });
}

/**
 * Add a member to the team
 * Uses: POST /teams/:id/members
 */
export function useUpdateMemberRole(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateMemberRoleInput) => {
      const { memberId, role } = validateSchema(UpdateMemberRoleSchema, data);
      const response = await apiClient.patch(
        `/teams/${teamId}/members/${memberId}`,
        { role },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamKeys.detailWithMembers(teamId),
      });
    },
  });
}

/**
 * Leave a team you are a member of (self-service removal of own membership).
 * Uses: DELETE /teams/:id/members/:userId
 */
export function useLeaveTeam(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/teams/${teamId}/members/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.teams() });
    },
  });
}

/**
 * Remove a member from the team
 * Uses: DELETE /teams/:id/members/:userId
 */
export function useRemoveMember(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await apiClient.delete(`/teams/${teamId}/members/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamKeys.detailWithMembers(teamId),
      });
    },
  });
}
